import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Avatar,
  Paper,
  Stack,
  CircularProgress,
  useTheme
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import Pagination from '../Pagination'
import noDataImg from '../../assets/websiteVisitor.png'
import SetupDialog from './SetupDialog'

const fetchVisitorsData = (page, limit) => new Promise(resolve => {
  setTimeout(() => {
    const total = 50
    const visitors = Array.from({ length: limit }, (_, i) => {
      const id = (page - 1) * limit + i + 1
      return {
        id,
        name: `Visitor ${id}`,
        title: `Title ${id}`,
        location: id % 2 ? `Location ${id}` : '',
        lastVisit: `Jul 11, ${6 + (id % 12)}:${id % 60} AM`,
        avatar: `/avatar${(id % 3) + 1}.png`
      }
    })

    resolve({ visitors, total })
  }, 1000)
})

const WebsiteVisitorsComponent = () => {
  const theme = useTheme()

  const [visitorsData, setVisitorsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState([])
  const [openDialog, setOpenDialog] = useState(false)

  const handleOpen = () => setOpenDialog(true)
  const handleClose = () => setOpenDialog(false)

  useEffect(() => {
    loadVisitorsData(page, limit)
  }, [page, limit])

  const loadVisitorsData = async (pageNum, pageSize) => {
    setLoading(true)
    const { visitors, total } = await fetchVisitorsData(pageNum, pageSize)
    setVisitorsData(visitors)
    setTotal(total)
    setSelected([])
    setLoading(false)
  }

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(visitorsData.map(v => v.id))
    } else {
      setSelected([])
    }
  }

  const handleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color='primary' />
      </Box>
    )
  }

  if (!loading && visitorsData.length === 0) {
    return (
      <>
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <img src={noDataImg} alt='No Data' width='300' />
          <Typography variant='h6'>No traffic analysis data available.</Typography>
          <Typography variant='body2' color='text.secondary' mb={3}>
            Track and capture visitor activity by integrating our tracking script.
          </Typography>
          <Button onClick={handleOpen} variant='contained'>
            Integrate Tracking Script
          </Button>
        </Box>
        <SetupDialog open={openDialog} onClose={handleClose} />
      </>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, border: `1px solid ${theme.palette.grey[300]}` }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent='space-between'
        >
          <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            <Checkbox
              size='small'
              checked={selected.length === visitorsData.length && visitorsData.length > 0}
              indeterminate={selected.length > 0 && selected.length < visitorsData.length}
              onChange={handleSelectAll}
            />
            <Typography variant='body1' fontWeight={500} color={theme.palette.primary.primaryText}>
              {total} website visitors
            </Typography>
            {/* <Chip
              label="C-Level"
              size="small"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
              }}
            /> */}
          </Box>
          <Button
            variant='outlined'
            startIcon={<FilterListIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              mt: { xs: 1, sm: 0 },
              borderColor: theme.palette.grey[300],
              color: theme.palette.primary.primaryText
            }}
          >
            Filter
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        {visitorsData.map(visitor => (
          <Paper
            elevation={2}
            key={visitor.id}
            sx={{
              p: { xs: 1.5, sm: 2 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              border: `1px solid ${theme.palette.grey[200]}`
            }}
          >
            <Box display='flex' alignItems='center'>
              <Checkbox
                size='small'
                checked={selected.includes(visitor?.id)}
                onChange={() => handleSelect(visitor?.id)}
              />
              <Avatar src={visitor?.avatar} sx={{ ml: 1 }} />
            </Box>

            <Box flexGrow={1} mt={{ xs: 1, sm: 0 }}>
              <Typography variant='body1' fontWeight={600} width={300}>
                {visitor?.name}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {visitor?.title}
              </Typography>
            </Box>

            <Box
              textAlign={{ xs: 'left', sm: 'right' }}
              mt={{ xs: 1, sm: 0 }}
              minWidth={{ sm: '150px' }}
              width='100%'
            >
              {visitor?.location && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  display='flex'
                  alignItems='center'
                  justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                  gap={0.5}
                >
                  <LocationOnOutlinedIcon fontSize='small' />
                  {visitor.location}
                </Typography>
              )}
              <Typography
                variant='body2'
                color='text.secondary'
                display='flex'
                alignItems='center'
                justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                gap={0.5}
              >
                <AccessTimeOutlinedIcon fontSize='small' />
                Last Visit: {visitor?.lastVisit}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Pagination
          limit={limit}
          handleLimitChange={(e) => {
            setLimit(e.target.value)
            setPage(1)
          }}
          page={page}
          length={visitorsData.length}
          total={total}
          setPage={setPage}
        />
      </Box>
    </Box>
  )
}

export default WebsiteVisitorsComponent
